import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, X, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocumentUploader = ({ files = [], onChange, purpose = 'tender_document', maxFiles = 5, accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png' }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    if (files.length + selectedFiles.length > maxFiles) {
      toast.error(`Max ${maxFiles} files allowed`);
      return;
    }
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(selectedFiles)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}/uploads/document?purpose=${purpose}`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push({ url: data.url, path: data.path, filename: data.filename, size_bytes: data.size_bytes });
      } catch (err) {
        const detail = err.response?.data?.detail || `Failed to upload ${file.name}`;
        toast.error(typeof detail === 'string' ? detail : 'Upload failed');
      }
    }
    setUploading(false);
    if (uploaded.length) {
      onChange([...files, ...uploaded]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    }
  };

  const removeFile = (idx) => onChange(files.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3" data-testid="doc-uploader">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="doc-uploader-input"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading || files.length >= maxFiles}
        className="px-4 py-2 rounded-md border-2 border-dashed border-input hover:border-navy/50 text-sm inline-flex items-center disabled:opacity-50"
        data-testid="doc-uploader-btn"
      >
        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        {uploading ? 'Uploading...' : `Attach Files (${files.length}/${maxFiles})`}
      </button>
      <p className="text-xs text-muted-foreground">PDF / DOC / Image · Max 10 MB each</p>

      {files.length > 0 && (
        <ul className="space-y-1" data-testid="doc-uploader-list">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between p-2 rounded bg-muted/30 text-sm" data-testid={`doc-uploaded-${i}`}>
              <div className="flex items-center min-w-0">
                <FileText className="h-4 w-4 mr-2 text-navy flex-shrink-0" />
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="truncate hover:underline text-navy">
                  {f.filename}
                </a>
                {f.size_bytes && <span className="ml-2 text-xs text-muted-foreground flex-shrink-0">{(f.size_bytes / 1024).toFixed(1)} KB</span>}
              </div>
              <button type="button" onClick={() => removeFile(i)} className="ml-2 text-red-600 hover:bg-red-50 p-1 rounded" data-testid={`doc-remove-${i}`}>
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DocumentUploader;
